/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipSet, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule, MatPseudoCheckboxModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { debounceTime, first, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { Validations } from '../..';

@Component({
	selector: 'multiselect-autocomplete',
	styleUrls: ['./multiselect-autocomplete.component.less'],
	templateUrl: './multiselect-autocomplete.component.html',
	standalone: true,
	imports: [
		MatFormFieldModule,
		NgClass,
		MatChipsModule,
		NgFor,
		MatIconModule,
		MatInputModule,
		FormsModule,
		MatAutocompleteModule,
		ReactiveFormsModule,
		FormlyModule,
		NgIf,
		MatOptionModule,
		MatPseudoCheckboxModule,
		MatCheckboxModule,
		MatButtonModule,
		AsyncPipe,
	],
})
export class MultiselectAutocompleteComponent extends FieldType<FormlyFieldConfig> implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
	@ViewChild(MatChipSet) chipList: MatChipSet;
	@ViewChild('inputRef', { static: false }) inputRef: ElementRef<HTMLInputElement>;

	public itemControl = new UntypedFormControl();

	selectedItems = [];
	filteredItems$ = this.itemControl.valueChanges.pipe(
		startWith(this.itemControl.value),
		debounceTime(250),
		switchMap((search) =>
			this.props
				.filter(search)
				.pipe(
					map((items: any[]) =>
						items.map((item) =>
							this.selectedItems.findIndex((si) => this.props.comparerFunc(item, si)) >= 0
								? { ...item, Selected: true }
								: { ...item, Selected: false }
						)
					)
				)
		)
	);
	selectAll = false;

	public noRecordsFound$ = this.filteredItems$.pipe(map((items: unknown[]) => this.itemControl.value && items.length < 1));

	private readonly destroy$ = new Subject();

	private readonly itemControlTouched$ = new BehaviorSubject<boolean>(false);

	public hasSecondLine = false;

	constructor(private readonly zone: NgZone) {
		super();
	}

	public ngOnInit(): void {
		if (!this.props.comparerFunc || !this.props.displaySelectedFunc || !this.props.filterDisplayValueFunc || !this.props.filter) {
			throw Error(
				'comparerFunc & displaySelectedFunc & filterDisplayValueFunc & filter in order to multiselect autocomplete input work corretly!'
			);
		}

		if (this.props.selectedItems.length !== 0) {
			this.selectedItems = this.props.selectedItems;
		} else {
			this.selectedItems = Array.isArray(this.props.options) ? this.props.options.filter((el) => el.Selected) : [];
		}

		if (this.props.hasSecondLine) {
			this.hasSecondLine = true;
		}
	}

	public ngAfterViewInit(): void {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const self = this;
		const origFunc = this.itemControl.markAsTouched;
		// eslint-disable-next-line space-before-function-paren
		this.itemControl.markAsTouched = function () {
			// eslint-disable-next-line prefer-rest-params
			origFunc.apply(this, arguments);
			self.itemControlTouched$.next(self.itemControl.touched);
		};

		combineLatest([this.itemControlTouched$.pipe(startWith(false)), this.formControl.statusChanges.pipe(startWith(this.formControl.status))])
			.pipe(
				takeUntil(this.destroy$),
				map(([touched, status]) => touched && status === 'INVALID')
			)
			.subscribe((errorState) => {
				this.formControl.setErrors({ invalid: errorState });
			});

		if (this.props.dropCustomValues) {
			this.autocomplete.autocomplete.displayWith = this.displayFn.bind(this);
		}
	}

	get state() {
		if (this.selectAll) {
			return 'checked';
		} else if (this.selectedItems.length > 0) {
			return 'indeterminate';
		} else {
			return '';
		}
	}

	toggleSelectAll() {
		this.selectAll = !this.selectAll;
		if (this.selectAll) {
			this.filteredItems$.pipe(first()).subscribe((items) => {
				this.selectedItems = (items as any[]).map((item) => ({ ...item, Selected: true }));
				this.writeValue();
				this.resetInput();
				if (this.props.changeFn) {
					this.props.changeFn(this.selectedItems, null);
				}
			});
		} else {
			this.selectedItems = [];
			this.writeValue();
			this.resetInput();
			if (this.props.changeFn) {
				this.props.changeFn(this.selectedItems, null);
			}
		}
	}

	public displayFn(value: any): any {
		if (!value) {
			return null;
		}

		return this.props.filterDisplayValueFunc ? this.props.filterDisplayValueFunc(value) : value;
	}

	public displayFn2(value: any): any {
		if (!value) {
			return null;
		}

		return this.props.filterDisplayValueFunc2 ? this.props.filterDisplayValueFunc2(value) : value;
	}

	public remove(item: any): void {
		if (this.props.readonly) {
			return;
		}
		if (this.selectAll) {
			this.selectAll = false;
		}
		item.Selected = !item.Selected;
		const i = this.selectedItems.findIndex((value) => this.props.comparerFunc(value, item));
		this.selectedItems.splice(i, 1);

		if (this.props.changeFn) {
			this.props.changeFn(this.selectedItems, item);
		}
		this.writeValue();
		this.resetInput();
	}

	public optionClicked(event: Event, item: any): void {
		event.stopPropagation();
		if (!this.props.readonly) {
			this.toggleSelection(item);
		}
	}

	public toggleSelection(item: any): void {
		item.Selected = !item.Selected;
		if (item.Selected) {
			this.selectedItems.push(item);
		} else {
			const i = this.selectedItems.findIndex((value) => this.props.comparerFunc(value, item));
			this.selectedItems.splice(i, 1);
		}

		if (this.props.changeFn) {
			this.props.changeFn(this.selectedItems, item);
		}
		this.writeValue();
		this.resetInput();
		this.filteredItems$.pipe(first()).subscribe((filteredItems) => {
			const selectedCount = this.selectedItems.length;
			const filteredCount = (filteredItems as any[]).length;
			this.selectAll = selectedCount === filteredCount && selectedCount > 0;
		});
	}

	public resetInput() {
		this.itemControl.setValue('');
		// that's how angular team deals with bugs - https://github.com/angular/components/issues/8214
		this.inputRef.nativeElement.value = '';
	}

	public getErrorMessage(): string {
		return this.formControl.errors?.required ? Validations.requiredValidationMessage({}, this.field) : '';
	}

	public ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	private writeValue() {
		this.formControl.setValue(this.selectedItems && this.selectedItems.length > 0 ? this.selectedItems : null);
		this.zone.onStable.pipe(first()).subscribe(() => {
			if (this.autocomplete?.panelOpen) {
				this.autocomplete?.updatePosition();
			}
		});
	}
}
